import { Box, Divider, Flex, HStack, StackDivider, Tag, Text, VStack } from "@chakra-ui/react";
import { useMemo } from "react";
import { Rating } from "react-simple-star-rating";
import TagList from "@components/ChakraUI/TagList";
import { getAddress } from "@utils/customerUtils";
import { useTranslatedCompetence } from "@utils/hooks";
import ConsultantImage from "@components/ConsultantImage";
import LoadingIndicator from "@components/Loading";
import AddressIcon from "@components/icons/AddressIcon";
import ManStandingIcon from "@components/icons/ManStandingIcon";
import StarIcon from "@components/icons/StarIcon";
import { useConsultantAccount } from "@api/hooks";
import { appTheme } from "@styles/Theme";

interface ConsultantMiniProfileProps {
	id: string;
	previouslyHired: boolean;
}

const ConsultantMiniProfile = ({ id, previouslyHired }: ConsultantMiniProfileProps) => {
	const { consultantAccount, isLoading } = useConsultantAccount(id, ["ratings"]);

	const translatedCompetences = useTranslatedCompetence(consultantAccount?.profile.competences);

	const skills = useMemo(() => {
		return (translatedCompetences?.map((c) => c.skillsNames || []).flat() || []).sort((a, b) =>
			(a.sv || a.key).localeCompare(b.sv || b.key),
		);
	}, [translatedCompetences]);

	return (
		<VStack>
			{isLoading && <LoadingIndicator size={"s"} />}
			{!isLoading && consultantAccount && (
				<>
					<Flex my={2} direction={"column"} alignItems={"center"} gap="1.5">
						<ConsultantImage url={consultantAccount.profile.profilePicture} name={consultantAccount.firstName} />

						<Text fontSize={"20px"} fontWeight={600} my={0}>
							{consultantAccount.firstName}
						</Text>
						{previouslyHired && (
							<Tag bg={"#4BB769"} color={"#FFFFFF"} width={"fit-content"}>
								Tidigare anlitad
							</Tag>
						)}
						{!!consultantAccount.profile.ratings?.average && (
							<Text fontSize={"18px"} fontWeight={500} my={0}>
								{consultantAccount.profile.ratings?.average ?? ""}
							</Text>
						)}
						{!!consultantAccount.profile.ratings?.average && (
							<Rating
								initialValue={consultantAccount.profile.ratings?.average ?? 0}
								size={20}
								readonly
								fillColor={appTheme.colors.accent}
								disableFillHover
								SVGstyle={{ display: "inline" }}
							/>
						)}
						{!!consultantAccount.profile.ratings?.count && (
							<Text fontSize={"13px"} fontWeight={400} my={0} color={"#999999"}>
								{`${consultantAccount.profile.ratings?.count} omdömen`}
							</Text>
						)}
					</Flex>
					<Divider />
					<Box my={2}>
						<TagList
							tagsAlignment="center"
							tagMargin={0}
							maxVisibleTags={10}
							tagList={skills.map((c) => ({
								key: c.key || "",
								value: c.sv || c.key,
							}))}
						/>
					</Box>
					<Text fontSize={"16px"} textAlign={"center"} fontWeight={400} color={"#999999"} my={2}>
						{consultantAccount.profile.presentation}
					</Text>
					<VStack
						divider={<StackDivider borderColor="gray.200" />}
						spacing={1}
						align="stretch"
						mr="auto"
						width={"90%"}
						my={2}
					>
						<HStack h="40px">
							<StarIcon />
							<Text fontSize={"16px"} fontWeight={400} my={0}>
								{`${consultantAccount.profile.noOfJobs ?? 0} uppdrag`}
							</Text>
						</HStack>
						<HStack h="40px">
							<ManStandingIcon />
							<Text fontSize={"16px"} fontWeight={400} my={0}>{`${consultantAccount.profile.age} år gammal`}</Text>
						</HStack>
						<HStack h="40px">
							<AddressIcon />
							<Text fontSize={"16px"} fontWeight={400} my={0}>
								{consultantAccount.profile.address ? getAddress(consultantAccount.profile.address) : "--"}
							</Text>
						</HStack>
					</VStack>
				</>
			)}
		</VStack>
	);
};

export default ConsultantMiniProfile;
